import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Form, Spinner } from "react-bootstrap"
import { Phone, Mail } from "lucide-react";
import API_BASE_URL from "../../config/api";
import logo from '../../assets/Byvallogo.png';

const Payslip = ({ employeeId, id, download = false }) => {
  const [payslip, setPayslip] = useState(null);
  const payslipRef = useRef();
  const [monthName, setMonthName] = useState("");

  useEffect(() => {
    const token = sessionStorage.getItem("token");

    axios
      .get(`${API_BASE_URL}/api/Payroll/Employeepayslip`, {
        params: { employeeId, id },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {

        setPayslip(res.data);
        setMonthName(
          new Date(2000, res.data.month - 1, 1).toLocaleString("default", { month: "long" })
        );

      })
      .catch((err) => console.error(err));
  }, [employeeId, id]);

  const formatCurrency = (value) =>
    value?.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) || "0.00";

    const handleDownload = async () => {
      const element = payslipRef.current;
    
      // Temporarily apply PDF-specific layout
      element.style.display = "flex";
      element.style.flexDirection = "column";
      element.style.justifyContent = "space-between";
      element.style.minHeight = "980px"; // slightly less to avoid clipping
      element.style.paddingTop = "0"; // no top empty space
      element.style.marginTop = "0";
    
      // Ensure footer has visible border-top
      const footer = element.querySelector(".payslip-footer");
      if (footer) {
        footer.style.borderTop = "1px solid #000";
        footer.style.paddingBottom = "12px";
        footer.style.marginTop = "auto";
      }
    
      // Add row spacing for better rendering
      element.querySelectorAll("tr").forEach(row => row.classList.add("pdf-row-space"));
      element.querySelectorAll(".detail").forEach(row => row.classList.add("pdf-download-row-space"));
    
      const pdf = new jsPDF("p", "mm", "a5");
      const pageWidth = pdf.internal.pageSize.getWidth();
    
      // Capture payslip as an image
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        scrollY: 0, // prevent scroll offset issues
      });
    
      const imgData = canvas.toDataURL("image/jpeg", 0.5);
    
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
      // Start exactly from top (no top margin)
      pdf.addImage(imgData, "JPEG", 0, 0, imgWidth, imgHeight, undefined, "FAST");
    
      pdf.save(`Payslip_${monthName}_${payslip.year}.pdf`);
    
      // ✅ Revert layout after download
      element.style.display = "";
      element.style.flexDirection = "";
      element.style.justifyContent = "";
      element.style.minHeight = "";
      element.style.paddingTop = "";
      element.style.marginTop = "";
    
      if (footer) {
        footer.style.borderTop = "";
        footer.style.paddingTop = "";
        footer.style.marginTop = "";
      }
    
      element.querySelectorAll("tr").forEach(row => row.classList.remove("pdf-row-space"));
      element.querySelectorAll(".detail").forEach(row => row.classList.remove("pdf-download-row-space"));
    };
    



  useEffect(() => {
    if (download && payslip) {
      const timer = setTimeout(() => {
        handleDownload();
      }, 500); // wait for DOM render
      return () => clearTimeout(timer);
    }
  }, [download, payslip]);

  if (!payslip) return <div className="p-6">Loading payslip...</div>;

  return (
    <div className="flex flex-col items-center py-5 px-2">
      <div
        ref={payslipRef}
        className="bg-white text-black px-10 w-[700px] font-sans text-sm flex flex-col space-between"
      >
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="w-40 h-40 flex items-center justify-center">
              <img src={logo} alt="Company Logo" className="max-h-full max-w-full object-contain" />
            </div>

            <div className="text-right">
              <h1 className="font-bold text-lg">ByVal Technologies India Pvt Ltd.</h1>
              <p className="text-xs whitespace-pre-line">
                {payslip.companyAddress ||
                  `No 13/42A Harisudhan Buildings, 
TexPark Road, Nehru Nagar West, 
Civil Aerodrome PO, Coimbatore - 641 014.`}
              </p>
              <div className="flex justify-end items-center gap-4 mt-1 text-xs">
                <div className="flex items-center gap-1 detail">
                  <Phone size={14} /> <span>+91-8248474232</span>
                </div>
                <div className="flex items-center gap-1 detail">
                  <Mail size={14} /> <span>info@byval.in</span>
                </div>
              </div>
            </div>
          </div>
          <p className="text-right mt-2">
            {new Date().toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            }).replace(/ /g, "-")}
          </p>
          {/* Payslip Header */}
          <h3 className="text-center my-5 font-semibold text-base">
            Pay Slip for {monthName} {payslip.year}
          </h3>

          {/* Employee Info */}
          <table className="w-full mb-6 text-sm">
            <tbody>
              <tr>
                <td className="font-semibold w-40 py-1">Employee ID:</td>
                <td>{payslip.employeeCode}</td>
              </tr>
              <tr>
                <td className="font-semibold py-1">Employee Name:</td>
                <td>{payslip.employeeName}</td>
              </tr>
              <tr>
                <td className="font-semibold py-1">Designation:</td>
                <td>{payslip.designation}</td>
              </tr>
            </tbody>
          </table>

          {/* Earnings and Deductions */}
          <table className="w-full border border-black border-collapse mb-6 text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-black px-2 py-2 text-left">Earnings</th>
                <th className="border border-black px-2 py-2 text-right">Amount (₹)</th>
                <th className="border border-black px-2 py-2 text-left">Deductions</th>
                <th className="border border-black px-2 py-2 text-right">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: Math.max(payslip.earnings.length, payslip.deductions.length) }).map(
                (_, idx) => (
                  <tr key={idx}>
                    <td className="border border-black px-2 py-1">
                      {payslip.earnings[idx]?.type || ""}
                    </td>
                    <td className="border border-black px-2 py-1 text-right">
                      {payslip.earnings[idx] ? `${formatCurrency(payslip.earnings[idx].amount)}` : ""}
                    </td>
                    <td className="border border-black px-2 py-1">
                      {payslip.deductions[idx]?.type || ""}
                    </td>
                    <td className="border border-black px-2 py-1 text-right">
                      {payslip.deductions[idx] ? `${formatCurrency(payslip.deductions[idx].amount)}` : ""}
                    </td>
                  </tr>
                )
              )}

              {/* Totals */}
              <tr>
                <td className="border border-black px-2 py-1 font-bold">Total Earnings</td>
                <td className="border border-black px-2 py-1 font-bold text-right">
                  {formatCurrency(payslip.totalEarnings)}
                </td>
                <td className="border border-black px-2 py-1 font-bold">Total Deductions</td>
                <td className="border border-black px-2 py-1 font-bold text-right">
                  {formatCurrency(payslip.totalDeductions)}
                </td>
              </tr>
              <tr>
                <td colSpan="2" className="border border-black px-2 py-1 font-bold">
                </td>
                <td colSpan="1" className="border border-black px-2 py-1 font-bold">
                  Net Pay (₹)
                </td>
                <td colSpan="1" className="border border-black px-2 py-1 font-bold text-right">
                  {formatCurrency(payslip.netPay)}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Remarks */}
          <p className="mb-12 text-sm font-semibold">Remarks:</p>
        </div>
        {/* Footer */}
        <div className="flex justify-between text-xs text-gray-600 payslip-footer">
  <p className="text-right">
    Generated on:{" "}
    {new Date(payslip.createdOn)
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, "-")}
  </p>

  <p>Electronically generated. No signature required</p>
</div>


      </div>

      {/* Download Button */}
      <div className="mt-4">
        {/* <button
          className="btn-black"
          onClick={handleDownload}
        >
          Download Payslip
        </button> */}

        <button
          className="btn-black"
          onClick={handleDownload}
        >
          {
            download ?
              <><Spinner animation="border" size="sm" style={{ width: '1rem', height: '1rem' }} /> Downloading...</>
              :
              'Download Payslip'
          }

        </button>
      </div>
    </div>
  );
};

export default Payslip;
